import React from 'react';
import { Link, Outlet } from 'react-router';

import { useAbilityContext } from '@/contexts/ability';

import { useCreateUrl } from '@/hooks/useNavigate';
import { SettingsModulesSidebar } from '@/features/settings/components/SettingsModulesSidebar';

import { t } from 'i18next';
import { ArrowRightSLine } from '@datanomik/core-ui/icons';
import { useSettingsMenu } from './hooks';
import { Portal, Separator, SlotPortal, Tabs, TabsList, TabsTrigger, Text, cn } from '@datanomik/core-ui';
interface SettingsMenuLayoutProps {
  className?: string;
  location?: string;
}

export const SettingsMenuLayout = (props: SettingsMenuLayoutProps) => {
  const { className, location } = props;

  const createUrl = useCreateUrl();
  const ability = useAbilityContext();

  const {
    routeItem,
    currentTab,
    isMobile,
    isMobileMd,
    showTabletTabs,
    sidebarMenuElement,
  } = useSettingsMenu({ location });

  const {
    route: { children: menuItems, label },
  } = routeItem;

  const classes = cn({
    [className]: !!className,
  });

  const visibleItems = (menuItems as Record<string, any>[])
    .filter(({ showInMenu, key }) => showInMenu && ability.can('view', key) && ability.can('enabled', key))
    .map(({
      path: childPath,
      icon: childIcon,
      label: childLabel,
      key: childKey,
      link: childLink,
    }) => ({
      key: childKey,
      label: childLabel,
      icon: childIcon,
      to: childLink || createUrl(`/${location}/${childPath}`),
    }));

  if (!isMobile) {
    return (
      <div className={classes}>
        <div className="flex flex-row gap-6 items-start w-full">
          <SettingsModulesSidebar
            items={visibleItems}
            currentTab={currentTab}
            menuLabel={label}
          />
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    );
  }

  const menu = (orientation) => (
    <Tabs
      value={currentTab}
      variant={orientation === 'vertical' ? 'plain' : 'underline'}
      orientation={orientation}
      size={orientation === 'vertical' ? 'md' : 'sm'}
    >
      <TabsList>
        {visibleItems.map(({ key, label: itemLabel, icon, to }) => (
          <TabsTrigger key={key} value={to} asChild>
            <Link to={to} {...(to.startsWith('http') ? { target: '_blank' } : {})}>
              {icon}
              <span className="flex-1 text-left">{t(itemLabel)}</span>
              <ArrowRightSLine />
            </Link>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );

  return (
    <div className={classes}>
      {sidebarMenuElement && !isMobileMd && (
        <Portal container={sidebarMenuElement}>
          <div className="flex flex-col w-66 h-full p-5 gap-5 shrink-0 border-r border-border bg-card">
            <div className="flex flex-col gap-1">
              <Text variant="lead" weight="medium">
                {t(label)}
              </Text>
              <Text tone="muted">
                {t('Browse between categories')}
              </Text>
            </div>
            <Separator />
            {menu('vertical')}
          </div>
        </Portal>
      )}

      {isMobile && (
        <SlotPortal name="bottom-tabs-mobile">
          {menu('horizontal')}
        </SlotPortal>
      )}

      {showTabletTabs && (
        <SlotPortal name="bottom-tabs-desktop">
          {menu('horizontal')}
        </SlotPortal>
      )}

      <Outlet />
    </div>
  );
};
